import React from 'react';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './PhoneInput.css';

const CommonForm = ({
  title,
  fields,
  values,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  errors = {},
  requiredFields = [],
  openMapPicker,
  countriesData = [], // Add countries data prop (processed for dropdown)
  rawCountriesData = [], // Add raw countries data prop (from API)
  onBusinessAddressChange,
  businessDropdowns,
}) => {
  const handleFieldChange = (name, value) => {
    onChange({
      ...values,
      [name]: value,
    });

    if (errors[name]) {
      delete errors[name];
    }
  };

  // Helper function to check if field is a phone/mobile field (only for basic tab mobileNumber)
  const isPhoneField = (fieldName, fieldType) => {
    return fieldName === 'mobileNumber' && fieldType === 'phoneWithCountry';
  };

  // Helper function to get country code from country data (Dynamic API-based) - only for basic tab mobileNumber
  const getCountryCodeFromAPI = (countryId) => {
    if (!countryId) return 'in'; // default to India
    
    // First priority: Use raw countries data directly from API (most accurate)
    if (rawCountriesData.length > 0) {
      const country = rawCountriesData.find(c => c._id === countryId);
      if (country && country.short_code) {
        console.log('Found country from API raw data:', country.name, country.short_code);
        return country.short_code.toLowerCase();
      }
    }
    
    // Second priority: Use processed countries data and try to match with raw data by name
    if (countriesData.length > 0 && rawCountriesData.length > 0) {
      const processedCountry = countriesData.find(c => c.value === countryId);
      if (processedCountry) {
        // Try to find matching country in raw data by name
        const rawCountry = rawCountriesData.find(rc => 
          rc.name.toLowerCase() === processedCountry.label.toLowerCase()
        );
        if (rawCountry && rawCountry.short_code) {
          console.log('Found country by name matching:', rawCountry.name, rawCountry.short_code);
          return rawCountry.short_code.toLowerCase();
        }
      }
    }
    
    console.log('No country found, defaulting to India');
    return 'in'; // default fallback
  };

  // Helper function to get country code for phone input (only for basic tab mobileNumber)
  const getPhoneCountryCode = (fieldName) => {
    // Only apply dynamic country code for basic tab mobile number field
    if (fieldName === 'mobileNumber' && values.country && (countriesData.length > 0 || rawCountriesData.length > 0)) {
      const countryCode = getCountryCodeFromAPI(values.country);
      console.log('Phone country code:', countryCode, 'for country ID:', values.country, 'field:', fieldName);
      return countryCode;
    }
    console.log('Using default India code for field:', fieldName);
    return 'in'; // default to India for all other mobile fields
  };

  // Support for section-wise rendering
  const isSectioned = Array.isArray(fields) && fields.length > 0 && fields[0].fields && fields[0].section;

  return (
    <div className="p-2 rounded shadow-sm border border-light bg-white">
     {/*  <h4 className="mb-0 border-bottom pb-2 text-dark">{title}</h4> */}
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        {isSectioned ? (
          fields.map((section, idx) => (
            <div key={section.section || idx} className="mb-2">
              {/* Remove section name for specific sections */}
              {['Profile', 'Documents', 'Contact', 'Login', 'Personal Details', 'Address', 'Membership','Business Details'].includes(section.section) ? null : (
                <h5 className="mb-2 text-primary" style={{ fontWeight: 600 }}>{section.section}</h5>
              )}
              <div className="row">
                {section.fields.map((field) => {
                  // --- Use the same field rendering logic as before ---
                  const isRequired = requiredFields.includes(field.name);
                  const fieldError = errors[field.name];
                  const addressConditionalFields = ['state', 'city', 'country'];
                  let showRequiredField = isRequired;
                  if (addressConditionalFields.includes(field.name)) {
                    showRequiredField = !!(values.address && values.address.trim() !== '');
                  }

                  // Children and Businesses special handling
                  if (field.name === 'children') {
                    // ...existing code for children...
                    return (
                      <div className="col-12 mb-2" key="children">
                        <label className="form-label fw-semibold d-block">
                          {field.label}
                          {isRequired && <span className="text-danger"> *</span>}
                        </label>
                        {values.children?.map((child, index) => (
                          <div key={index} className="row g-2 align-items-end mb-2">
                            <div className="col-md-3">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Child Name"
                                value={child.name}
                                onChange={(e) => {
                                  const updated = [...values.children];
                                  updated[index].name = e.target.value;
                                  onChange({ ...values, children: updated });
                                }}
                              />
                            </div>
                            <div className="col-md-3">
                              <input
                                type="date"
                                className="form-control"
                                value={child.dob}
                                onChange={(e) => {
                                  const updated = [...values.children];
                                  updated[index].dob = e.target.value;
                                  onChange({ ...values, children: updated });
                                }}
                              />
                            </div>
                            <div className="col-md-3">
                              <div className="d-flex gap-2 align-items-center">
                                {[
                                  { value: 'Male', label: 'Male' },
                                  { value: 'Female', label: 'Female' },
                                ].map((genderOption) => (
                                  <div className="form-check" key={genderOption.value}>
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name={`gender-${index}`}
                                      id={`gender-${genderOption.value}-${index}`}
                                      value={genderOption.value}
                                      checked={child.gender === genderOption.value}
                                      onChange={(e) => {
                                        const updated = [...values.children];
                                        updated[index].gender = e.target.value;
                                        onChange({ ...values, children: updated });
                                      }}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`gender-${genderOption.value}-${index}`}
                                    >
                                      {genderOption.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="col-md-3 d-flex gap-1">
                              {index === values.children.length - 1 ? (
                                <button
                                  type="button"
                                  className="btn btn-success"
                                  onClick={() => {
                                    const updated = [...values.children];
                                    updated.push({ name: '', dob: '', gender: '' });
                                    onChange({ ...values, children: updated });
                                  }}
                                >
                                  +
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => {
                                    const updated = values.children.filter((_, i) => i !== index);
                                    onChange({ ...values, children: updated });
                                  }}
                                >
                                  -
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  if (field.name === 'businesses') {
                    // ...existing code for businesses...
                    return (
                      <div className="col-12 mb-2" key="businesses">
                        <label className="form-label fw-semibold d-block">
                          {field.label || ''}
                          {isRequired && <span className="text-danger"> *</span>}
                        </label>
                        {values.businesses?.map((biz, index) => (
                          <div key={index} className="border p-3 mb-2 rounded">
                            <h6 className="mb-2 text-secondary">Business #{index + 1}</h6>
                            {/* Section: Basic Details (no heading) */}
                            <div className="row mb-2">
                              {['business_name','primary_business','business_established_date','business_type','business_category','business_sub_category','business_formation','business_product_service','team_size'].map((fname) => {
                                const subField = (field.fields || []).find(f => f.name === fname);
                                if (!subField) return null;
                                const fieldName = subField.name;
                                const fieldKey = `businesses[${index}].${fieldName}`;
                                const fieldValue = biz[fieldName] || (subField.type === 'checkbox' ? false : '');
                                const fieldError = errors[fieldKey];
                                let showRequired = requiredFields.includes(fieldKey);
                                const conditionalFields = [
                                  'business_email',
                                  'business_type',
                                  'business_category',
                                  'business_sub_category',
                                  'business_formation',
                                  'business_product_service',
                                ];
                                if (conditionalFields.includes(subField.name)) {
                                  const businessName = biz['business_name'] || '';
                                  showRequired = !!businessName.trim();
                                }
                                return (
                                  <div className="col-md-3 mb-2" key={fieldKey}>
                                    {subField.type === 'checkbox' ? (
                                      <div className="form-check mt-0 d-flex align-items-center" style={{ height: '38px' }}>
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={fieldKey}
                                          checked={!!fieldValue}
                                          onChange={(e) => {
                                            const updated = [...values.businesses];
                                            updated[index][fieldName] = e.target.checked;
                                            onChange({ ...values, businesses: updated });
                                          }}
                                        />
                                        {/* Only show label if present and not empty */}
                                        {subField.label ? (
                                          <label className="form-check-label ms-2" htmlFor={fieldKey}>
                                            {subField.label}
                                          </label>
                                        ) : null}
                                      </div>
                                    ) : (
                                      <>
                                        <label className="form-label fw-semibold">
                                          {subField.label || subField.placeholder}
                                          {showRequired && (
                                            <span className="text-danger"> *</span>
                                          )}
                                        </label>
                                        {subField.type === 'select' ? (
                                          <Select
                                            options={subField.options}
                                            placeholder={subField.placeholder}
                                            isMulti={subField.isMulti || false}
                                            value={
                                              subField.isMulti
                                                ? subField.options.filter((opt) =>
                                                    Array.isArray(fieldValue) && fieldValue.includes(opt.value)
                                                  )
                                                : subField.options.find((opt) => opt.value === fieldValue) || null
                                            }
                                            onChange={(selected) => {
                                              const updated = [...values.businesses];
                                              if (subField.isMulti) {
                                                const selectedValues = selected ? selected.map((opt) => opt.value) : [];
                                                updated[index][fieldName] = selectedValues;
                                              } else {
                                                updated[index][fieldName] = selected ? selected.value : '';
                                              }
                                              onChange({ ...values, businesses: updated });
                                            }}
                                            classNamePrefix="select"
                                          />
                                        ) : (
                                          <input
                                            type={subField.type}
                                            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                            placeholder={subField.placeholder}
                                            value={fieldValue}
                                            onChange={(e) => {
                                              const updated = [...values.businesses];
                                              updated[index][fieldName] = e.target.value;
                                              onChange({ ...values, businesses: updated });
                                            }}
                                          />
                                        )}
                                      </>
                                    )}
                                    {conditionalFields.includes(subField.name)
                                      ? (biz['business_name'] && !biz[subField.name]
                                          ? (
                                              <div className="invalid-feedback d-block">{subField.label} is required when Business Name is filled.</div>
                                            )
                                          : (biz['business_name'] && fieldError
                                              ? <div className="invalid-feedback d-block">{fieldError}</div>
                                              : null)
                                        )
                                      : fieldError && (
                                          <div className="invalid-feedback d-block">{fieldError}</div>
                                        )}
                                  </div>
                                );
                              })}
                            </div>
                            <hr className="my-2" />
                            <div className="row mb-2">
                              {['business_email','mobile','gst_number','website_url'].map((fname) => {
                                const subField = (field.fields || []).find(f => f.name === fname);
                                if (!subField) return null;
                                const fieldName = subField.name;
                                const fieldKey = `businesses[${index}].${fieldName}`;
                                const fieldValue = biz[fieldName] || '';
                                const fieldError = errors[fieldKey];
                                // Only require business_email if business_name is filled
                                const isBusinessEmail = subField.name === 'business_email';
                                const businessNameFilled = (biz['business_name'] || '').trim() !== '';
                                const showRequired = isBusinessEmail ? businessNameFilled : false;
                                return (
                                  <div className="col-md-3 mb-2" key={fieldKey}>
                                    <label className="form-label fw-semibold">
                                      {subField.label || subField.placeholder}
                                      {showRequired && <span className="text-danger"> *</span>}
                                    </label>
                                    <input
                                      type={subField.type}
                                      className={`form-control ${(isBusinessEmail && businessNameFilled && !fieldValue) || fieldError ? 'is-invalid' : ''}`}
                                      placeholder={subField.placeholder}
                                      value={fieldValue}
                                      onChange={(e) => {
                                        const updated = [...values.businesses];
                                        updated[index][fieldName] = e.target.value;
                                        onChange({ ...values, businesses: updated });
                                      }}
                                    />
                                    {/* Show error only if business_name is filled and business_email is empty, otherwise show other errors */}
                                    {isBusinessEmail && businessNameFilled && !fieldValue
                                      ? <div className="invalid-feedback d-block">Business Email is required when Company Name is filled.</div>
                                      : fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
                                  </div>
                                );
                              })}
                            </div>
                            <hr className="my-2" />
                            <div className="row mb-2">
                              {['business_description'].map((fname) => {
                                const subField = (field.fields || []).find(f => f.name === fname);
                                if (!subField) return null;
                                const fieldName = subField.name;
                                const fieldKey = `businesses[${index}].${fieldName}`;
                                const fieldValue = biz[fieldName] || '';
                                const fieldError = errors[fieldKey];
                                return (
                                  <div className="col-md-6 mb-2" key={fieldKey}>
                                    <label className="form-label fw-semibold">
                                      {subField.label || subField.placeholder}
                                    </label>
                                    <textarea
                                      className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                      rows={2}
                                      value={fieldValue}
                                      onChange={(e) => {
                                        const updated = [...values.businesses];
                                        updated[index][fieldName] = e.target.value;
                                        onChange({ ...values, businesses: updated });
                                      }}
                                      style={{ resize: 'none' }}
                                    />
                                    {fieldError && (
                                      <div className="invalid-feedback d-block">{fieldError}</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <hr className="my-2" />
                            <div className="row mb-2">
                              {['logo','banner','gstImage'].map((fname) => {
                                const subField = (field.fields || []).find(f => f.name === fname);
                                if (!subField) return null;
                                const fieldName = subField.name;
                                const fieldKey = `businesses[${index}].${fieldName}`;
                                const fieldValue = biz[fieldName] || '';
                                const fieldError = errors[fieldKey];
                                return (
                                  <div className="col-md-3 mb-2" key={fieldKey}>
                                    <label className="form-label fw-semibold">
                                      {subField.label || subField.placeholder}
                                    </label>
                                    {fieldValue ? (
                                      <div className="mb-2">
                                        <img
                                          src={typeof fieldValue === 'string' ? fieldValue : URL.createObjectURL(fieldValue)}
                                          alt="Preview"
                                          className="img-thumbnail"
                                          style={{ maxWidth: '150px', height: 'auto' }}
                                        />
                                        <div>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-danger mt-1"
                                            onClick={() => {
                                              const updated = [...values.businesses];
                                              updated[index][fieldName] = '';
                                              onChange({ ...values, businesses: updated });
                                            }}
                                          >
                                            x
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <input
                                        type="file"
                                        className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                        onChange={(e) => {
                                          const updated = [...values.businesses];
                                          updated[index][fieldName] = e.target.files[0];
                                          onChange({ ...values, businesses: updated });
                                        }}
                                      />
                                    )}
                                    {fieldError && (
                                      <div className="invalid-feedback d-block">{fieldError}</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <hr className="my-2" />
                            <div className="row mb-2">
                              <div className="col-md-6 mb-2">
                                <label className="form-label fw-semibold">Business Address</label>
                                <textarea
                                  className="form-control"
                                  rows={2}
                                  placeholder="Enter business address"
                                  value={biz.address || ''}
                                  onChange={(e) => {
                                    const updated = [...values.businesses];
                                    updated[index].address = e.target.value;
                                    onChange({ ...values, businesses: updated });
                                  }}
                                  style={{ resize: 'none' }}
                                />
                              </div>
                              <div className="col-md-6 mb-2">
                                <label className="form-label fw-semibold">Business Location (GPS)</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  readOnly
                                  value={
                                    biz.latitude && biz.longitude
                                      ? `${parseFloat(biz.latitude).toFixed(6)}, ${parseFloat(biz.longitude).toFixed(6)}`
                                      : ''
                                  }
                                  onClick={() => openMapPicker && openMapPicker(index)}
                                  placeholder="Click to select business location"
                                  style={{ cursor: 'pointer' }}
                                />
                                {biz.latitude && biz.longitude && (
                                  <small className="text-muted">
                                    Lat: {parseFloat(biz.latitude).toFixed(6)}, Lng: {parseFloat(biz.longitude).toFixed(6)}
                                  </small>
                                )}
                              </div>
                            </div>
                            <div className="row mb-2">
                              {['country','state','city'].map((fname) => {
                                const subField = (field.fields || []).find(f => f.name === fname);
                                if (!subField) return null;
                                const fieldName = subField.name;
                                const fieldKey = `businesses[${index}].${fieldName}`;
                                const fieldValue = biz[fieldName] || '';
                                const fieldError = errors[fieldKey];
                                let options = subField.options;
                                if (fieldName === 'state' && businessDropdowns) {
                                  options = businessDropdowns[index]?.states || [];
                                }
                                if (fieldName === 'city' && businessDropdowns) {
                                  options = businessDropdowns[index]?.cities || [];
                                }
                                return (
                                  <div className="col-md-4 mb-2" key={fieldKey}>
                                    <label className="form-label fw-semibold">
                                      {subField.label || subField.placeholder}
                                    </label>
                                    <Select
                                      options={options}
                                      placeholder={subField.placeholder}
                                      value={options.find((opt) => opt.value === fieldValue) || null}
                                      onChange={(selected) => {
                                        const updated = [...values.businesses];
                                        updated[index][fieldName] = selected ? selected.value : '';
                                        onChange({ ...values, businesses: updated });
                                        if (['country', 'state'].includes(fieldName) && typeof onBusinessAddressChange === 'function') {
                                          onBusinessAddressChange(fieldName, selected ? selected.value : '', index);
                                        }
                                      }}
                                      classNamePrefix="select"
                                    />
                                    {fieldError && (
                                      <div className="invalid-feedback d-block">{fieldError}</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <hr className="my-2" />
                            <div className="row mb-2">
                              {['instagram','google_profile','youtube','linkedin','facebook'].map((fname) => {
                                const subField = (field.fields || []).find(f => f.name === fname);
                                if (!subField) return null;
                                const fieldName = subField.name;
                                const fieldKey = `businesses[${index}].${fieldName}`;
                                const fieldValue = biz[fieldName] || '';
                                const fieldError = errors[fieldKey];
                                return (
                                  <div className="col-md-2 mb-2" key={fieldKey}>
                                    <label className="form-label fw-semibold">
                                      {subField.label || subField.placeholder}
                                    </label>
                                    <input
                                      type={subField.type}
                                      className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                      placeholder={subField.placeholder}
                                      value={fieldValue}
                                      onChange={(e) => {
                                        const updated = [...values.businesses];
                                        updated[index][fieldName] = e.target.value;
                                        onChange({ ...values, businesses: updated });
                                      }}
                                    />
                                    {fieldError && (
                                      <div className="invalid-feedback d-block">{fieldError}</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="d-flex justify-content-end">
                              {values.businesses.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => {
                                    const updated = values.businesses.filter((_, i) => i !== index);
                                    onChange({ ...values, businesses: updated });
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {/* Add button inside each section that contains businesses group */}
                        <button
                          type="button"
                          className="btn btn-success mt-2"
                          onClick={() => {
                            const updated = [
                              ...values.businesses,
                              Object.fromEntries((field.fields || []).map(f => [f.name, f.type === 'checkbox' ? false : ''])),
                            ];
                            onChange({ ...values, businesses: updated });
                          }}
                        >
                          + Add Business
                        </button>
                      </div>
                    );
                  }

                  // Standard field rendering
                  const col2Fields = ['address', 'country', 'state', 'city', 'pincode'];
                  return (
                    <div className={`mb-3${col2Fields.includes(field.name) ? ' col-md-2' : ' col-md-3'}`} key={field.name}>
                      <label className="form-label fw-semibold">
                        {field.label || field.placeholder}
                        {showRequiredField && <span className="text-danger"> *</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                          rows={field.rows || 2}
                          value={values[field.name] || ''}
                          onChange={(e) => handleFieldChange(field.name, e.target.value)}
                          style={{ resize: 'none' }}
                        />
                      ) : field.type === 'select' ? (
                        <Select
                          options={field.options}
                          placeholder={field.placeholder}
                          isMulti={field.isMulti || false}
                          value={
                            field.isMulti
                              ? field.options.filter((opt) =>
                                  Array.isArray(values[field.name]) && values[field.name].includes(opt.value)
                                )
                              : field.options.find((opt) => opt.value === values[field.name]) || null
                          }
                          onChange={(selected) => {
                            if (field.isMulti) {
                              const selectedValues = selected ? selected.map((opt) => opt.value) : [];
                              handleFieldChange(field.name, selectedValues);
                            } else {
                              handleFieldChange(field.name, selected ? selected.value : '');
                            }
                          }}
                          classNamePrefix="select"
                        />
                      ) : field.type === 'checkbox' ? (
                        <div className="form-check mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={field.name}
                            checked={values[field.name] || false}
                            onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor={field.name}>
                            {field.label}
                          </label>
                        </div>

                      ) : field.type === 'file' ? (
                        <>
                          {values[field.name] ? (
                            <div className="mb-2">
                              <img
                                src={typeof values[field.name] === 'string' ? values[field.name] : URL.createObjectURL(values[field.name])}
                                alt="Preview"
                                className="img-thumbnail"
                                style={{ maxWidth: '150px', height: 'auto' }}
                              />
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger mt-1"
                                  onClick={() => handleFieldChange(field.name, '')}
                                >
                                x
                                </button>
                              </div>
                            </div>
                          ) : (
                            <input
                              type="file"
                              className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                              onChange={(e) => handleFieldChange(field.name, e.target.files[0])}
                            />
                          )}
                        </>
                      ) : field.type === 'radio' && field.name === 'gender' ? (
                        <div className="d-flex gap-2 align-items-center">
                          {field.options.map((option) => (
                            <div className="form-check" key={option.value}>
                              <input
                                className="form-check-input"
                                type="radio"
                                name={field.name}
                                value={option.value}
                                checked={values[field.name] === option.value}
                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                              />
                              <label className="form-check-label">
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : isPhoneField(field.name, field.type) ? (
                        <PhoneInput
                          country={getPhoneCountryCode(field.name)}
                          value={values[field.name] || ''}
                          onChange={(phone, countryData) => {
                            console.log(`Phone changed for ${field.name}:`, phone, 'Country:', countryData);
                            handleFieldChange(field.name, phone);
                          }}
                          inputClass={`form-control ${fieldError ? 'is-invalid' : ''}`}
                          containerClass="phone-input-container"
                          buttonClass="phone-input-button"
                          dropdownClass="phone-input-dropdown"
                          placeholder="Enter mobile number"
                          enableSearch={true}
                          disableSearchIcon={true}
                        />
                      ) : field.type === 'basicLocation' && openMapPicker ? (
                        <div>
                          <input
                            type="text"
                            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                            readOnly
                            value={
                              values.latitude && values.longitude
                                ? `${parseFloat(values.latitude).toFixed(6)}, ${parseFloat(values.longitude).toFixed(6)}`
                                : ''
                            }
                            onClick={() => openMapPicker('basic')}
                            placeholder="Click to select location"
                            style={{ cursor: 'pointer' }}
                          />
                          {values.latitude && values.longitude && (
                            <small className="text-muted">
                              Lat: {parseFloat(values.latitude).toFixed(6)}, Lng: {parseFloat(values.longitude).toFixed(6)}
                            </small>
                          )}
                        </div>
                      ) : field.type === 'basicLocation' ? (
                        <input
                          type="text"
                          className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                          readOnly
                          value={
                            values.latitude && values.longitude
                              ? `${parseFloat(values.latitude).toFixed(6)}, ${parseFloat(values.longitude).toFixed(6)}`
                              : ''
                          }
                          onClick={() => openMapPicker && openMapPicker('basic')}
                          placeholder="Click to select location"
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (field.name === 'latitude' || field.name === 'longitude') && openMapPicker ? (
                        <div>
                          <input
                            type="text"
                            className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                            value={values[field.name] || ''}
                            readOnly
                            placeholder={`Click to select ${field.name}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openMapPicker('basic')}
                          />
                          {field.name === 'latitude' && values.latitude && values.longitude && (
                            <small className="text-muted">
                              Current Location: {parseFloat(values.latitude).toFixed(6)}, {parseFloat(values.longitude).toFixed(6)}
                            </small>
                          )}
                        </div>
                      ) : (
                        <input
                          type={field.type}
                          className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                          value={values[field.name] || ''}
                          onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        />
                      )}
                      {/* Show error for state, city, country only if address is filled, with custom message */}
                      {addressConditionalFields.includes(field.name)
                        ? (values.address && values.address.trim() !== '' && !values[field.name] ? (
                              <div className="invalid-feedback d-block">{field.label} is required when Address is filled.</div>
                          ) : (values.address && values.address.trim() !== '' && fieldError && (
                              <div className="invalid-feedback d-block">{fieldError}</div>
                          )))
                        : fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
                    </div>
                  );
                })}
              </div>
              {idx !== fields.length - 1 && <hr className="my-2" />}
            </div>
          ))
        ) : (
          <div className="row">
            {/* fallback to old rendering if not sectioned */}
            {fields.map((field) => {
              // ...existing code for non-sectioned fields...
              const isRequired = requiredFields.includes(field.name);
              const fieldError = errors[field.name];
              const addressConditionalFields = ['state', 'city', 'country'];
              let showRequiredField = isRequired;
              if (addressConditionalFields.includes(field.name)) {
                showRequiredField = !!(values.address && values.address.trim() !== '');
              }
              // Children and Businesses special handling
              if (field.name === 'children') {
                // ...existing code for children...
                return (
                  <div className="col-12 mb-2" key="children">
                    <label className="form-label fw-semibold d-block">
                      {field.label}
                      {isRequired && <span className="text-danger"> *</span>}
                    </label>
                    {values.children?.map((child, index) => (
                      <div key={index} className="row g-2 align-items-end mb-2">
                        <div className="col-md-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Child Name"
                            value={child.name}
                            onChange={(e) => {
                              const updated = [...values.children];
                              updated[index].name = e.target.value;
                              onChange({ ...values, children: updated });
                            }}
                          />
                        </div>
                        <div className="col-md-2">
                          <input
                            type="date"
                            className="form-control"
                            value={child.dob}
                            onChange={(e) => {
                              const updated = [...values.children];
                              updated[index].dob = e.target.value;
                              onChange({ ...values, children: updated });
                            }}
                          />
                        </div>
                        <div className="col-md-2">
                        <div className="d-flex gap-2 align-items-center">
                          {[
                            { value: 'Male', label: 'Male' },
                            { value: 'Female', label: 'Female' },
                          ].map((genderOption) => (
                            <div className="form-check" key={genderOption.value}>
                              <input
                                className="form-check-input"
                                type="radio"
                                name={`gender-${index}`}
                                id={`gender-${genderOption.value}-${index}`}
                                value={genderOption.value}
                                checked={child.gender === genderOption.value}
                                onChange={(e) => {
                                  const updated = [...values.children];
                                  updated[index].gender = e.target.value;
                                  onChange({ ...values, children: updated });
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`gender-${genderOption.value}-${index}`}
                              >
                                {genderOption.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                        <div className="col-md-2 d-flex gap-1">
                          {index === values.children.length - 1 ? (
                            <button
                              type="button"
                              className="btn btn-success"
                              onClick={() => {
                                const updated = [...values.children];
                                updated.push({ name: '', dob: '', gender: '' });
                                onChange({ ...values, children: updated });
                              }}
                            >
                              +
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => {
                                const updated = values.children.filter((_, i) => i !== index);
                                onChange({ ...values, children: updated });
                              }}
                            >
                              -
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              if (field.name === 'businesses') {
                // ...existing code for businesses...
                return (
                  <div className="col-12 mb-2" key="businesses">
                    <label className="form-label fw-semibold d-block">
                      {field.label || 'Businesses'}
                      {isRequired && <span className="text-danger"> *</span>}
                    </label>
                    {values.businesses?.map((biz, index) => (
                      <div key={index} className="border p-1 mb-2 rounded">
                        <h6 className="mb-2 text-secondary">Business #{index + 1}</h6>
                        <div className="row">
                          {(field.fields || []).map((subField) => {
                            // ...existing code for subField rendering...
                            const fieldName = subField.name;
                            const fieldKey = `businesses[${index}].${fieldName}`;
                            const fieldValue = biz[fieldName] || (subField.type === 'checkbox' ? false : '');
                            const fieldError = errors[fieldKey];
                            let showRequired = requiredFields.includes(fieldKey);
                            const conditionalFields = [
                              'business_email',
                              'business_type',
                              'business_category',
                              'business_sub_category',
                              'business_formation',
                              'business_product_service',
                            ];
                            if (conditionalFields.includes(subField.name)) {
                              const businessName = biz['business_name'] || '';
                              showRequired = !!businessName.trim();
                            }
                            // ...existing code for subField rendering...
                            return (
                              <div className="col-md-2 mb-2" key={fieldKey}>
                                <label className="form-label fw-semibold">
                                  {subField.label || subField.placeholder}
                                  {showRequired && (
                                    <span className="text-danger"> *</span>
                                  )}
                                </label>
                                {subField.type === 'textarea' ? (
                                  <textarea
                                    className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                    rows={subField.rows || 3}
                                    value={fieldValue}
                                    onChange={(e) => {
                                      const updated = [...values.businesses];
                                      updated[index][fieldName] = e.target.value;
                                      onChange({ ...values, businesses: updated });
                                    }}
                                     style={{ resize: 'none' }}
                                  />
                                ) : subField.type === 'select' ? (
                                  <Select
                                    options={subField.options}
                                    placeholder={subField.placeholder}
                                    isMulti={subField.isMulti || false}
                                    value={
                                      subField.isMulti
                                        ? subField.options.filter((opt) =>
                                            Array.isArray(fieldValue) && fieldValue.includes(opt.value)
                                          )
                                        : subField.options.find((opt) => opt.value === fieldValue) || null
                                    }
                                    onChange={(selected) => {
                                      const updated = [...values.businesses];
                                      if (subField.isMulti) {
                                        const selectedValues = selected ? selected.map((opt) => opt.value) : [];
                                        updated[index][fieldName] = selectedValues;
                                      } else {
                                        updated[index][fieldName] = selected ? selected.value : '';
                                      }
                                      onChange({ ...values, businesses: updated });
                                    }}
                                    classNamePrefix="select"
                                  />
                                ) : subField.type === 'checkbox' ? (
                                  <div className="form-check mt-2">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={fieldKey}
                                      checked={!!fieldValue}
                                      onChange={(e) => {
                                        const updated = [...values.businesses];
                                        updated[index][fieldName] = e.target.checked;
                                        onChange({ ...values, businesses: updated });
                                      }}
                                    />
                                    <label className="form-check-label" htmlFor={fieldKey}>
                                      {subField.label}
                                    </label>
                                  </div>
                                ) : subField.type === 'file' ? (
                                  <>
                                    {fieldValue ? (
                                      <div className="mb-2">
                                        <img
                                          src={typeof fieldValue === 'string' ? fieldValue : URL.createObjectURL(fieldValue)}
                                          alt="Preview"
                                          className="img-thumbnail"
                                          style={{ maxWidth: '150px', height: 'auto' }}
                                        />
                                        <div>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-danger mt-1"
                                            onClick={() => {
                                              const updated = [...values.businesses];
                                              updated[index][fieldName] = '';
                                              onChange({ ...values, businesses: updated });
                                            }}
                                          >
                                            x
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <input
                                        type="file"
                                        className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                        onChange={(e) => {
                                          const updated = [...values.businesses];
                                          updated[index][fieldName] = e.target.files[0];
                                          onChange({ ...values, businesses: updated });
                                        }}
                                      />
                                    )}
                                  </>
                                ) : (
                                  <input
                                    type={subField.type}
                                    className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                                    placeholder={subField.placeholder}
                                    value={fieldValue}
                                    onChange={(e) => {
                                      const updated = [...values.businesses];
                                      updated[index][fieldName] = e.target.value;
                                      onChange({ ...values, businesses: updated });
                                    }}
                                  />
                                )}
                                {/* Show error for conditional business fields only if business_name is filled, otherwise show other errors as usual */}
                                {conditionalFields.includes(subField.name)
                                  ? (biz['business_name'] && !biz[subField.name]
                                      ? (
                                          <div className="invalid-feedback d-block">{subField.label} is required when Business Name is filled.</div>
                                        )
                                      : (biz['business_name'] && fieldError
                                          ? <div className="invalid-feedback d-block">{fieldError}</div>
                                          : null)
                                    )
                                  : fieldError && (
                                      <div className="invalid-feedback d-block">{fieldError}</div>
                                    )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="d-flex justify-content-end">
                          {values.businesses.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => {
                                const updated = values.businesses.filter((_, i) => i !== index);
                                onChange({ ...values, businesses: updated });
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-success mt-2"
                      onClick={() => {
                        const updated = [
                          ...values.businesses,
                          Object.fromEntries((field.fields || []).map(f => [f.name, f.type === 'checkbox' ? false : ''])),
                        ];
                        onChange({ ...values, businesses: updated });
                      }}
                    >
                      + Add Business
                    </button>
                  </div>
                );
              }
              // Standard field rendering
              return (
                <div className="col-md-3 mb-2" key={field.name}>
                  <label className="form-label fw-semibold">
                    {field.label || field.placeholder}
                    {showRequiredField && <span className="text-danger"> *</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                      rows={field.rows || 2}
                      value={values[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      style={{ resize: 'none' }}
                    />
                  ) : field.type === 'select' ? (
                    <Select
                      options={field.options}
                      placeholder={field.placeholder}
                      isMulti={field.isMulti || false}
                      value={
                        field.isMulti
                          ? field.options.filter((opt) =>
                              Array.isArray(values[field.name]) && values[field.name].includes(opt.value)
                            )
                          : field.options.find((opt) => opt.value === values[field.name]) || null
                      }
                      onChange={(selected) => {
                        if (field.isMulti) {
                          const selectedValues = selected ? selected.map((opt) => opt.value) : [];
                          handleFieldChange(field.name, selectedValues);
                        } else {
                          handleFieldChange(field.name, selected ? selected.value : '');
                        }
                      }}
                      classNamePrefix="select"
                    />
                  ) : field.type === 'checkbox' ? (
                    <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={field.name}
                        checked={values[field.name] || false}
                        onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={field.name}>
                        {field.label}
                      </label>
                    </div>
                  ) : field.type === 'file' ? (
                    <>
                      {values[field.name] ? (
                        <div className="mb-2">
                          <img
                            src={typeof values[field.name] === 'string' ? values[field.name] : URL.createObjectURL(values[field.name])}
                            alt="Preview"
                            className="img-thumbnail"
                            style={{ maxWidth: '150px', height: 'auto' }}
                          />
                          <div>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger mt-1"
                              onClick={() => handleFieldChange(field.name, '')}
                            >
                            x
                            </button>
                          </div>
                        </div>
                      ) : (
                        <input
                          type="file"
                          className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                          onChange={(e) => handleFieldChange(field.name, e.target.files[0])}
                        />
                      )}
                    </>
                  ) : field.type === 'radio' && field.name === 'gender' ? (
                    <div className="d-flex gap-2 align-items-center">
                      {field.options.map((option) => (
                        <div className="form-check" key={option.value}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name={field.name}
                            value={option.value}
                            checked={values[field.name] === option.value}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                          />
                          <label className="form-check-label">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : isPhoneField(field.name, field.type) ? (
                    <PhoneInput
                      country={getPhoneCountryCode()}
                      value={values[field.name] || ''}
                      onChange={(phone) => handleFieldChange(field.name, phone)}
                      inputClass={`form-control ${fieldError ? 'is-invalid' : ''}`}
                      containerClass="phone-input-container"
                      buttonClass="phone-input-button"
                      dropdownClass="phone-input-dropdown"
                      placeholder="Enter mobile number"
                      enableSearch={true}
                      disableSearchIcon={true}
                    />
                  ) : (
                    <input
                      type={field.type}
                      className={`form-control ${fieldError ? 'is-invalid' : ''}`}
                      value={values[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    />
                  )}
                  {/* Show error for state, city, country only if address is filled, with custom message */}
                  {addressConditionalFields.includes(field.name)
                    ? (values.address && values.address.trim() !== '' && !values[field.name] ? (
                          <div className="invalid-feedback d-block">{field.label} is required when Address is filled.</div>
                      ) : (values.address && values.address.trim() !== '' && fieldError && (
                          <div className="invalid-feedback d-block">{fieldError}</div>
                      )))
                    : fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
                </div>
              );
            })}
          </div>
        )}
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="submit"
            className="btn"
            style={{ backgroundColor: '#C44C23', color: 'white', border: 'none' }}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommonForm;
